/* Rundis theme: core (dialogs, cart, delivery estimate, search, header). Vanilla JS, no dependencies. */
(() => {
  'use strict';
  const T = (window.Theme = window.Theme || {});
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const debounce = (fn, ms) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };
  T.$ = $; T.$$ = $$; T.reduceMotion = reduceMotion;

  /* ---------- Dialogs (drawers, modals) ---------- */
  let opener = null;
  T.openDialog = (dialog, from) => {
    if (!dialog || dialog.open) return;
    opener = from || document.activeElement;
    dialog.showModal();
    dialog.dispatchEvent(new CustomEvent('dialog:open', { bubbles: true }));
  };
  document.addEventListener('click', (e) => {
    const openBtn = e.target.closest('[data-open]');
    if (openBtn) {
      const d = document.getElementById(openBtn.dataset.open);
      if (d) { e.preventDefault(); T.openDialog(d, openBtn); }
      return;
    }
    if (e.target.closest('[data-close]')) {
      const d = e.target.closest('dialog');
      if (d) { e.preventDefault(); d.close(); }
      return;
    }
    const t = e.target;
    if (t.tagName === 'DIALOG' && t.open && e.clientX) {
      const r = t.getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) t.close();
    }
  });
  document.addEventListener('close', (e) => {
    if (e.target.tagName === 'DIALOG' && opener && document.contains(opener)) {
      opener.focus({ preventScroll: true });
      opener = null;
    }
  }, true);

  /* ---------- Auto-submit (language / country selectors) ---------- */
  document.addEventListener('change', (e) => {
    if (e.target.matches('[data-autosubmit]') && e.target.form) e.target.form.submit();
  });

  /* ---------- Copy to clipboard ---------- */
  document.addEventListener('click', async (e) => {
    const b = e.target.closest('[data-copy]');
    if (!b) return;
    try {
      await navigator.clipboard.writeText(b.dataset.copy);
      const label = $('[data-copy-label]', b);
      if (label) label.textContent = T.strings.copied;
    } catch (err) { /* clipboard unavailable: code stays visible */ }
  });

  /* ---------- Header scroll shadow ---------- */
  const header = $('[data-header]');
  if (header) {
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 4);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---------- Announcement bar ---------- */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.msgs = $$('.announcement__msg', this);
      this.i = 0;
      if (this.msgs.length < 2) return;
      this.delay = (Number(this.dataset.speed) || 5) * 1000;
      this.addEventListener('click', (e) => {
        const b = e.target.closest('[data-dir]');
        if (b) { this.go(this.i + Number(b.dataset.dir)); this.start(); }
      });
      ['mouseenter', 'focusin'].forEach((ev) => this.addEventListener(ev, () => this.stop()));
      ['mouseleave', 'focusout'].forEach((ev) => this.addEventListener(ev, () => this.start()));
      this.start();
    }
    disconnectedCallback() { this.stop(); }
    select(el) {
      const n = this.msgs.indexOf(el);
      if (n > -1) { this.stop(); this.go(n); }
    }
    go(n) {
      this.msgs[this.i].classList.remove('is-active');
      this.msgs[this.i].setAttribute('aria-hidden', 'true');
      this.i = (n + this.msgs.length) % this.msgs.length;
      this.msgs[this.i].classList.add('is-active');
      this.msgs[this.i].removeAttribute('aria-hidden');
    }
    start() {
      this.stop();
      if (reduceMotion || this.dataset.autoplay === 'false') return;
      this.timer = setInterval(() => this.go(this.i + 1), this.delay);
    }
    stop() { clearInterval(this.timer); }
  }
  customElements.define('announcement-bar', AnnouncementBar);
  document.addEventListener('shopify:block:select', (e) => {
    const bar = e.target.closest('announcement-bar');
    if (bar && bar.select) bar.select(e.target);
  });

  /* ---------- Desktop dropdown menus: close on outside click / Escape ---------- */
  document.addEventListener('click', (e) => {
    $$('.nav__dropdown[open]').forEach((d) => { if (!d.contains(e.target)) d.removeAttribute('open'); });
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    $$('.nav__dropdown[open]').forEach((d) => { d.removeAttribute('open'); $('summary', d).focus(); });
  });

  /* ---------- Delivery estimate (business days, localized dates) ---------- */
  const Delivery = {
    add(date, days) {
      const d = new Date(date.getTime());
      let n = 0;
      while (n < days) {
        d.setDate(d.getDate() + 1);
        const w = d.getDay();
        if (w !== 0 && w !== 6) n += 1;
      }
      return d;
    },
    range(country) {
      const cfg = T.delivery || {};
      const r = cfg[country] || cfg.other || [7, 14];
      const now = new Date();
      const day = now.getDay();
      const late = now.getHours() >= (cfg.cutoff ?? 14) || day === 0 || day === 6;
      const extra = late ? 1 : 0;
      return [this.add(now, Number(r[0]) + extra), this.add(now, Number(r[1]) + extra)];
    },
    fmt(d) {
      try {
        return new Intl.DateTimeFormat(T.locale, { weekday: 'short', day: 'numeric', month: 'short' }).format(d);
      } catch (e) {
        return d.toLocaleDateString();
      }
    },
    render(root = document) {
      $$('[data-delivery-estimate]', root).forEach((el) => {
        const tpl = el.dataset.template;
        if (!tpl) return;
        const [a, b] = this.range(el.dataset.country || T.country);
        el.textContent = tpl.replace('[from]', this.fmt(a)).replace('[to]', this.fmt(b));
      });
    }
  };
  T.Delivery = Delivery;

  /* ---------- Cart (Cart API + Section Rendering API) ---------- */
  const Cart = {
    sectionIds() {
      const ids = $$('[data-cart-section]').map((el) => el.dataset.cartSection);
      if ($('#CartDrawer')) ids.unshift('cart-drawer');
      return [...new Set(ids)];
    },
    async request(url, body, isForm) {
      const opts = { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } };
      if (isForm) {
        opts.body = body;
      } else {
        opts.headers['Content-Type'] = 'application/json';
        opts.body = JSON.stringify(body);
      }
      const res = await fetch(url, opts);
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status) throw new Error(data.description || data.message || T.strings.cartError);
      return data;
    },
    async add(form) {
      const fd = new FormData(form);
      const ids = this.sectionIds();
      if (ids.length) {
        fd.append('sections', ids.join(','));
        fd.append('sections_url', window.location.pathname);
      }
      const data = await this.request(`${T.routes.cartAdd}.js`, fd, true);
      this.render(data.sections);
      return data;
    },
    async change(line, quantity) {
      const ids = this.sectionIds();
      const data = await this.request(`${T.routes.cartChange}.js`, {
        line,
        quantity,
        sections: ids.join(','),
        sections_url: window.location.pathname
      });
      this.render(data.sections);
      return data;
    },
    render(sections) {
      if (!sections) return;
      Object.values(sections).forEach((html) => {
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        $$('[data-cart-region]', doc).forEach((fresh) => {
          const cur = $(`[data-cart-region="${fresh.dataset.cartRegion}"]`);
          if (cur) cur.replaceWith(document.importNode(fresh, true));
        });
        const count = $('[data-cart-count-value]', doc);
        if (count) this.setCount(count.dataset.cartCountValue);
      });
      Delivery.render();
      document.dispatchEvent(new CustomEvent('cart:updated'));
    },
    setCount(n) {
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = n;
        el.hidden = Number(n) === 0;
      });
    },
    open() {
      const d = $('#CartDrawer');
      if (d) T.openDialog(d);
    }
  };
  T.Cart = Cart;

  // Add to cart without leaving the page (falls back to a normal form post without JS / drawer)
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!form.matches('[data-product-form]') || T.cartType !== 'drawer' || !$('#CartDrawer')) return;
    e.preventDefault();
    const btn = e.submitter || $('[type="submit"]', form);
    const err = $('[data-form-error]', form);
    if (err) err.hidden = true;
    if (btn) { btn.setAttribute('aria-busy', 'true'); btn.disabled = true; }
    try {
      await Cart.add(form);
      if (!$('[data-cart-region="page"]')) Cart.open();
      form.dispatchEvent(new CustomEvent('product:added', { bubbles: true }));
    } catch (error) {
      if (err) { err.textContent = error.message; err.hidden = false; }
    } finally {
      if (btn) { btn.removeAttribute('aria-busy'); btn.disabled = false; }
    }
  });

  // Quantity buttons / inputs / remove links in cart drawer + cart page
  const updateLine = async (el, quantity) => {
    const region = el.closest('[data-cart-region]');
    const key = el.dataset.focusKey;
    const line = Number(el.dataset.line);
    if (region) region.setAttribute('aria-busy', 'true');
    try {
      await Cart.change(line, quantity);
      const next = key && $(`[data-focus-key="${key}"]`);
      if (next) next.focus();
      else {
        const title = $(`[data-cart-region="${region && region.dataset.cartRegion}"] [data-cart-title]`);
        if (title) title.focus();
      }
    } catch (error) {
      const msg = region && $('[data-cart-error]', region);
      if (msg) { msg.textContent = error.message; msg.hidden = false; }
      if (region) region.removeAttribute('aria-busy');
    }
  };
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-change]');
    if (!btn) return;
    e.preventDefault();
    updateLine(btn, Math.max(0, Number(btn.dataset.qtyChange)));
  });
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-line-qty]');
    if (input) updateLine(input, Math.max(0, parseInt(input.value, 10) || 0));
  });

  /* ---------- Predictive search ---------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = $('input[type="search"]', this);
      this.results = $('[data-search-results]', this);
      this.status = $('[data-search-status]', this);
      if (!this.input || !this.results) return;
      this.input.addEventListener('input', debounce(() => this.search(), 250));
    }
    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) { this.results.innerHTML = ''; return; }
      if (this.ctrl) this.ctrl.abort();
      this.ctrl = new AbortController();
      const url = `${T.routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product,page,article&resources[limit]=6&section_id=predictive-search`;
      try {
        const res = await fetch(url, { signal: this.ctrl.signal });
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = $('[data-predictive-results]', doc);
        this.results.innerHTML = fresh ? fresh.innerHTML : '';
        if (this.status && fresh) this.status.textContent = fresh.dataset.status || '';
      } catch (e) { /* aborted or offline */ }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ---------- Muted looping section videos (always with a pause button) ---------- */
  const initVideos = (root = document) => {
    $$('.video-section__video[data-autoplay]', root).forEach((v) => {
      const b = v.parentElement && $('[data-video-section-toggle]', v.parentElement);
      const set = () => { if (b) b.setAttribute('aria-pressed', String(v.paused)); };
      if (reduceMotion || !('IntersectionObserver' in window)) { v.controls = true; if (b) b.hidden = true; return; }
      new IntersectionObserver(([en]) => {
        if (en.isIntersecting && !v.dataset.userPaused) { const p = v.play(); if (p && p.catch) p.catch(() => {}); } else { v.pause(); }
        set();
      }, { threshold: 0.4 }).observe(v);
      if (b) b.addEventListener('click', () => {
        if (v.paused) { delete v.dataset.userPaused; v.play(); } else { v.dataset.userPaused = '1'; v.pause(); }
        set();
      });
    });
  };

  /* ---------- Print buttons ---------- */
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-print]')) { e.preventDefault(); window.print(); }
  });

  /* ---------- Init ---------- */
  Delivery.render();
  initVideos();
  const autofocus = $('[data-autofocus]');
  if (autofocus) autofocus.focus();
  document.addEventListener('shopify:section:load', (e) => { Delivery.render(e.target); initVideos(e.target); });
})();
